import { format, parseISO } from 'date-fns'
import ja from 'date-fns/locale/ja'
import React, { useEffect, useState, VFC } from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import styles from '../../styles/front/applicants/DividedDatePicker.module.sass'
import formStyles from '../../styles/front/form/Form.module.sass'
registerLocale('ja', ja)

type ClosedSchedule = {
  started_at: string
  finished_at: string
}

type EventSchedule = {
  id: string
  started_at: string
  finished_at: string
}

type DatePickerProps = {
  selectedDate: string
  startDate: string
  endDate: string
  openTime: string
  closeTime: string
  idName: string
  nameName: string
  className: string
  clearable: boolean
  errors?: string[]
  setDateTime: (date: string) => void
  setDate: (time: string) => void
  dateFilled: (bool: boolean) => void
  required?: boolean
  check: number
  errorTemp: string
  schedule_type: string
  closed_wday: string
  reservationSlotIntervalMinutes: number
  schedules?: EventSchedule[]
  closed_schedules?: ClosedSchedule[]
}

const formattedInputStartDate = (type: string, baseDateTime: string) => {
  if (baseDateTime) {
    const date = new Date(baseDateTime)
    if (type === 'date') {
      date.setHours(0)
      date.setMinutes(0)
      return date
    } else if (type === 'time') {
      const time = new Date()
      time.setHours(date.getHours())
      time.setMinutes(date.getMinutes())
      return time
    } else {
      return date
    }
  }
  return null
}

const createDateTime = (date: Date, time: string) => {
  const newDate = new Date(date.getTime())
  // 一旦timeをDateオブジェクトにして時刻を抽出する
  const timeDate = new Date(time)
  if (isNaN(timeDate.getTime())) {
    throw new Error(`不正な時刻形式です: ${time}`)
  }

  newDate.setHours(timeDate.getHours(), timeDate.getMinutes(), 0, 0)
  return newDate
}

const isNotHoliday = (date: Date, closedDays: string) => {
  if (!closedDays) {
    return true
  }
  const day = date.getDay()
  return !closedDays.split(',').map(Number).includes(day)
}

const isAvailableRegistration = (date: Date, started_at: string | undefined, finishedAt: string | undefined) => {
  if (started_at && finishedAt) {
    const firstDate = new Date(started_at)
    const lastDate = new Date(finishedAt)

    firstDate.setHours(0, 0, 0, 0)
    lastDate.setHours(23, 59, 59, 0)

    return firstDate <= date && date <= lastDate
  } else if (started_at && !finishedAt) {
    const firstDate = new Date(started_at)

    firstDate.setHours(0, 0, 0, 0)

    return firstDate <= date
  } else if (!started_at && finishedAt) {
    const lastDate = new Date(finishedAt)

    lastDate.setHours(23, 59, 59, 0)

    return date <= lastDate
  } else {
    return true
  }
}

const isClosedPeriod = (openDateTime: Date, closeDateTime: Date, closedSchedules: ClosedSchedule[] | undefined) => {
  if (!closedSchedules) {
    return false
  }
  for (const closedSchedule of closedSchedules) {
    const startedAt = new Date(closedSchedule.started_at)
    const finishedAt = new Date(closedSchedule.finished_at)
    if (startedAt <= openDateTime && closeDateTime < finishedAt) {
      return true
    }
  }
  return false
}

const isClosedPeriodForTime = (dateTime: Date, closedSchedules: ClosedSchedule[] | undefined) => {
  if (!closedSchedules) {
    return false
  }
  for (const closedSchedule of closedSchedules) {
    const startedAt = new Date(closedSchedule.started_at)
    const finishedAt = new Date(closedSchedule.finished_at)
    if (startedAt <= dateTime && dateTime < finishedAt) {
      return true
    }
  }
  return false
}

const reservationAvailableTime = (
  date: Date,
  openTime: string,
  closeTime: string
) => {
  const openReservationTime = createDateTime(date, openTime)
  const closeReservationTime = createDateTime(date, closeTime)
  return openReservationTime.getTime() <= date.getTime() && date.getTime() < closeReservationTime.getTime()
}

const isEventDay = (date: Date, schedules: any[] | undefined) => {
  if (!schedules) {
    return true
  }
  for (const schedule of schedules) {
    const startedAt = new Date(schedule.started_at)
    if (
      startedAt.getDate() == date.getDate() &&
      startedAt.getMonth() == date.getMonth() &&
      startedAt.getFullYear() == date.getFullYear()
    ) {
      return true
    }
  }
  return false
}

const isEventOpeningTime = (
  date: Date,
  schedules: any[] | undefined
) => {
  if (!schedules) {
    return true
  }
  for (const schedule of schedules) {
    const startedAt = new Date(schedule.started_at)
    const finishedAt = new Date(schedule.finished_at)
    if (startedAt.getTime() <= date.getTime() && date.getTime() < finishedAt.getTime()) {
      return true
    }
  }
  return false
}

const buildReservableTimesForPeriod = (startedAt: Date, finishedAt: Date, reservationSlotIntervalMinutes: number) => {
  if (finishedAt <= startedAt) {
    return []
  }

  const timeOptions: Date[] = []
  const currentTime = new Date(startedAt)

  while (currentTime < finishedAt) {
    timeOptions.push(new Date(currentTime))
    currentTime.setMinutes(currentTime.getMinutes() + reservationSlotIntervalMinutes)
  }

  return timeOptions
}

const buildTimeOptionsForSelectedDate = (
  selectedDate: Date | null,
  props: DatePickerProps
) => {
  if (!selectedDate) {
    return []
  }

  if (props.schedule_type === 'specified_date') {
    if (!props.schedules || props.schedules.length === 0) {
      if (!props.openTime || !props.closeTime) {
        return []
      }

      return buildReservableTimesForPeriod(
        createDateTime(selectedDate, props.openTime),
        createDateTime(selectedDate, props.closeTime),
        props.reservationSlotIntervalMinutes
      )
    }

    const reservableTimes = props.schedules.flatMap((schedule) => {
      const startedAt = new Date(schedule.started_at)
      const finishedAt = new Date(schedule.finished_at)

      if (
        startedAt.getFullYear() !== selectedDate.getFullYear() ||
        startedAt.getMonth() !== selectedDate.getMonth() ||
        startedAt.getDate() !== selectedDate.getDate()
      ) {
        return []
      }

      return buildReservableTimesForPeriod(startedAt, finishedAt, props.reservationSlotIntervalMinutes)
    })

    return Array.from(new Map(reservableTimes.map((time) => [time.getTime(), time])).values())
  }

  if (props.schedule_type === 'long_term') {
    if (!props.openTime || !props.closeTime) {
      return []
    }

    return buildReservableTimesForPeriod(
      createDateTime(selectedDate, props.openTime),
      createDateTime(selectedDate, props.closeTime),
      props.reservationSlotIntervalMinutes
    ).filter((time) => !isClosedPeriodForTime(time, props.closed_schedules))
  }

  return []
}

const DividedDatePicker: VFC<DatePickerProps> = (props) => {
  const DATEFORMAT = 'yyyy年MM月dd日(E)'
  const [selectedDateTime, setSelectedDateTime] = useState<Date | null>(
    formattedInputStartDate('datetime', props.selectedDate)
  )
  const [selectedDate, setSelectedDate] = useState<Date | null>(formattedInputStartDate('date', props.selectedDate))
  const [selectedTime, setSelectedTime] = useState<Date | null>(formattedInputStartDate('time', props.selectedDate))
  const isClearableFlag = props.clearable

  const [errorMsg, setErrorMsg] = useState<String>('')

  const tomorrow = () => {
    const dt = new Date()
    dt.setDate(dt.getDate() + 1)
    const startTime = new Date(props.startDate)
    dt.setHours(startTime.getHours(), startTime.getMinutes())
    return dt
  }

  const filterTime = (time: Date) => {
    if (selectedDate) {
      const inputTime = new Date(selectedDate)
      inputTime.setHours(time.getHours())
      inputTime.setMinutes(time.getMinutes())
      if (props.schedule_type === 'specified_date') {
        if (props.schedules?.length == 0) {
          return reservationAvailableTime(inputTime, props.openTime, props.closeTime)
        }
        return isEventOpeningTime(inputTime, props.schedules)
      } else if (props.schedule_type === 'long_term') {
        return (
          reservationAvailableTime(inputTime, props.openTime, props.closeTime) &&
          !isClosedPeriodForTime(inputTime, props.closed_schedules)
        )
      }
    }
    return false
  }

  const filterDate = (date: Date) => {
    if (props.schedule_type === 'specified_date') {
      if (props.schedules?.length == 0) {
        return isAvailableRegistration(date, props.startDate, props.endDate) && isNotHoliday(date, props.closed_wday)
      }
      return isEventDay(date, props.schedules)
    } else if (props.schedule_type === 'long_term') {
      const openDateTime = createDateTime(date, props.openTime)
      const closeDateTime = createDateTime(date, props.closeTime)

      return (
        isAvailableRegistration(date, props.startDate, props.endDate) &&
        isNotHoliday(date, props.closed_wday) &&
        !isClosedPeriod(openDateTime, closeDateTime, props.closed_schedules)
      )
    }
    return true
  }

  const timeOptions = buildTimeOptionsForSelectedDate(selectedDate, props)

  const isTimeAlert = () => {
    if (selectedDate && !selectedTime) {
      return true
    } else if (props.errors) {
      return true
    }
    return false
  }

  useEffect(() => {
    if (selectedDate) {
      let updatedDateTime = new Date(selectedDate)
      if (selectedTime) {
        updatedDateTime.setHours(selectedTime.getHours())
        updatedDateTime.setMinutes(selectedTime.getMinutes())
        props.dateFilled(true)
      }
      if (filterTime(updatedDateTime)) {
        setSelectedDateTime(updatedDateTime)
        props.dateFilled(true)
      } else {
        setSelectedTime(null)
        setSelectedDateTime(null)
        props.dateFilled(false)
      }
    } else {
      setSelectedDateTime(null)
      setSelectedTime(null)
      props.dateFilled(false)
    }
  }, [selectedTime, selectedDate])

  useEffect(() => {
    const selectDateTime = selectedDateTime?.toISOString() || ''
    props.setDateTime(selectDateTime)
  }, [selectedDateTime])

  useEffect(() => {
    if (props.required && props.check != 0 && !selectedDate) {
      setErrorMsg(props.errorTemp + '希望を入力してください')
    }
  }, [props.check])

  interface InputProps {
    idName: string
    nameName?: string
    className: string
    inputRef?: React.Ref<HTMLInputElement>
    onClick?: React.MouseEventHandler<HTMLInputElement>
    onChange?: React.ChangeEventHandler<HTMLInputElement>
    value?: string | ReadonlyArray<string> | number
    errors?: string[]
  }

  const Input = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
    return (
      <>
        <input
          type="text"
          id={props.idName}
          className={`${props.className} ${selectedDate && !props.errors ? formStyles.inputColor : ''}`}
          value={selectedDate != null ? format(selectedDate, DATEFORMAT, { locale: ja }) : ''}
          onClick={props.onClick}
          onChange={props.onChange}
          readOnly
          placeholder="日付を選択"
          inputMode="none"
        />
        {selectedDate && !props.errors && (
          <div className={`${formStyles.success} ${isClearableFlag ? formStyles.inner : ''}`}>
            <span className={'material-icons-outlined'}>done</span>
          </div>
        )}
      </>
    )
  })

  const handleSelectTime = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!e.target.value || e.target.value === '') {
      setSelectedTime(null)
      return
    } else {
      const time = new Date(e.target.value)
      setSelectedTime(time)
    }
    e.target.blur()
  }

  const changeDate = (date) => {
    setSelectedDate(date)
    props.setDate(date)
    setErrorMsg('')
  }

  const formattedDateTime = selectedDateTime ? format(selectedDateTime, 'yyyy-MM-dd HH:mm') : ''

  return (
    <div className={styles.dividedDatePicker}>
      <div className={styles.datePickerContainer}>
        <DatePicker
          locale="ja"
          selected={selectedDate}
          onChange={(date) => changeDate(date)}
          openToDate={
            new Date().getTime() > new Date(props.startDate).getTime() ? tomorrow() : parseISO(props.startDate)
          }
          minDate={tomorrow()}
          filterDate={filterDate}
          isClearable={isClearableFlag}
          placeholderText="日付を選択"
          dateFormat="yyyy年MM月dd日(E)"
          customInput={
            <Input
              idName={`${props.idName}-date`}
              className={
                props.className + ' ' + (errorMsg ? formStyles.errorInput + ' error' : isTimeAlert() ? 'error' : '')
              }
              errors={props.errors}
            />
          }
        />
        {props.errors && (
          <>
            {props.errors.map((error, index) => (
              <div className={`${formStyles.errorText} ${styles.errors}`} key={index}>
                {error}
              </div>
            ))}
          </>
        )}
        {errorMsg && <div className={`${formStyles.errorText} ${styles.errors}`}>{errorMsg}</div>}
      </div>
      <div className={`${formStyles.formSelectBox} ${formStyles.wide} ${errorMsg ? formStyles.spError : ''}`}>
        <select
          value={selectedTime ? selectedTime.toISOString() : ''}
          onChange={(e) => {
            handleSelectTime(e)
          }}
          className={`${styles.timeSelect} ${isTimeAlert() ? formStyles.errorInput : ''} ${
            selectedTime && !isTimeAlert() ? formStyles.inputColor : ''
          }`}
          disabled={!selectedDate}
        >
          <option value="" disabled={props.clearable ? false : true}>
            時間を選択
          </option>
          {timeOptions.map((time, i) => (
            <option key={i} value={time.toISOString()}>
              {time.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}
            </option>
          ))}
        </select>
        <div className="alert-box">
          {isTimeAlert() && !props.errors && (
            <div className={`${formStyles.errorText} ${styles.errors}`}>時間を入力してください</div>
          )}
        </div>
      </div>
      <input type="hidden" name={props.nameName} value={formattedDateTime} id={props.idName} />
    </div>
  )
}

export default DividedDatePicker
